import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { getCachedState } from '~store/utils';
import { saveTab, tryCloseTab } from '../root/thunks';
import { sanitize } from './adapter';
import { fetchRoles, fetchRolesChanges, saveRoles } from './thunks';
import { IRolesState, TById, TRawData, TRolesDict } from './types';
import { ROLES_TAB_PATH } from '@qodly/shared';

const stateSlice = createSlice({
  name: 'roles',
  initialState: (): IRolesState => {
    const { roles: cachedRoles } = getCachedState();
    const {
      roles: savedRoles = {},
      privileges: savedPriviliges = {},
      flags = { touched: false },
      date,
      selectedTab = 'roles',
    }: IRolesState = cachedRoles || {};

    return {
      flags: flags,
      roles: savedRoles,
      privileges: savedPriviliges,
      date,
      selectedTab,
    };
  },
  reducers: {
    updatePrivileges: (state, action: PayloadAction<TById>) => {
      state.privileges = action.payload;
      state.flags.touched = true;
    },
    updateRoles: (state, action: PayloadAction<TRolesDict>) => {
      state.roles = action.payload;
      state.flags.touched = true;
    },
    updateRolesAndPrivileges: (
      state,
      action: PayloadAction<{ roles: TRolesDict; privileges: TById }>,
    ) => {
      state.roles = action.payload.roles;
      state.privileges = action.payload.privileges;
    },
    toggleTab: (state, action: PayloadAction<'privileges' | 'roles'>) => {
      state.selectedTab = action.payload;
    },
    toggleForceLogin: (state, action: PayloadAction<boolean>) => {
      if (state.forceLogin === action.payload) return;
      state.forceLogin = action.payload;
      state.flags.touched = true;
    },
    setRolesContent(
      state,
      action: PayloadAction<{
        content: TRawData;
        date?: string;
        flags?: IRolesState['flags'];
      }>,
    ) {
      const { content, flags: changedFlags = {}, ...rest } = action.payload;

      const roles = sanitize(content);
      const flags = {
        ...state.flags,
        ...changedFlags,
      };

      return {
        ...state,
        ...roles,
        ...rest,
        flags,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchRoles.fulfilled, (state, action) => {
        const { content: roles, date } = action.payload;

        state = {
          ...state,
          ...roles,
          date,
        };
      })
      .addCase(fetchRoles.rejected, (state) => {
        return {
          ...state,
          roles: {},
          privileges: {},
          flags: { touched: false },
          date: undefined,
        };
      })
      .addCase(saveRoles.fulfilled, (state, action) => {
        return {
          ...state,
          flags: {
            ...state.flags,
            confirmed: true,
            touched: false,
            old: false,
          },
          date: action.payload.date,
        };
      })
      .addCase(saveRoles.rejected, (state, action) => {
        if (_.isString(action.payload)) return state;

        return {
          ...state,
          flags: {
            ...state.flags,
            ...action.payload,
          },
        };
      })
      .addCase(fetchRolesChanges.fulfilled, (state, action) => {
        return {
          ...state,
          flags: {
            ...state.flags,
            ...action.payload,
          },
        };
      })
      .addCase(saveTab.fulfilled, (state, action) => {
        if (!action.payload || action.payload.path !== ROLES_TAB_PATH) return;

        state.flags.touched = false;
        state.flags.old = false;
      })
      .addCase(tryCloseTab.fulfilled, (state, action) => {
        const path = action.meta.arg;
        if (path !== ROLES_TAB_PATH || !action.payload) return;

        state.flags.touched = false;
        state.flags.old = false;
      });
  },
});

export const {
  updatePrivileges,
  updateRoles,
  setRolesContent,
  toggleTab,
  toggleForceLogin,
  updateRolesAndPrivileges,
} = stateSlice.actions;

export default stateSlice;
